/**
 * Shared UI primitives, MCOS V3.
 *
 * Built from the client's design system (`mcos-v3-design-system.html`, Brian,
 * 2026-09-12): Steel / Sage / Amber, Manrope + Inter, a 4px spacing scale and
 * exactly three glass levels. The tokens themselves live in `src/index.css`;
 * this file only composes them.
 *
 * Every export that existed before V3 still exists with the same props. The
 * appearance changed; the contract did not. That is what lets fifteen pages
 * adopt the new look without fifteen rewrites.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { bandColor, bandPillClass, formatDelta } from '../lib/format';

/* ------------------------------------------------------------------ *
 * Layout
 * ------------------------------------------------------------------ */

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
      <div>
        {/* His spec: "H1: 34px, weight 480", navy as in every mockup. */}
        <h1 className="font-display text-[34px] leading-tight text-navy tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-[17px] text-ink-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 [&_a]:whitespace-nowrap [&_button]:whitespace-nowrap">{actions}</div>}
    </div>
  );
}

/**
 * Standard glass, the default surface for a card, a patient row, a stat tile.
 * `level="prominent"` is for page-level panels and modals only.
 */
export function Card({
  children,
  className = '',
  title,
  subtitle,
  action,
  padded = true,
  level = 'standard',
}: {
  children: ReactNode;
  className?: string;
  title?: string;
  subtitle?: string;
  action?: ReactNode;
  padded?: boolean;
  level?: 'standard' | 'prominent';
}) {
  return (
    <section
      className={`${level === 'prominent' ? 'glass-prominent' : 'glass-standard'} rounded-md2 ${className}`}
    >
      {(title || action) && (
        <header className="flex flex-wrap items-start justify-between gap-3 px-6 pt-5 pb-3">
          {/* A floor on the title so a phone moves the action under it instead of squeezing the words. */}
          <div className="min-w-[200px] flex-1">
            {/* "H3 / card titles: 18px, weight 560." */}
            {title && <h2 className="font-display text-[18px] font-semibold text-navy">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-[13.5px] text-ink-muted">{subtitle}</p>}
          </div>
          {action}
        </header>
      )}
      <div className={padded ? 'p-5' : ''}>{children}</div>
    </section>
  );
}

/**
 * A page-level panel. Prominent glass plus the single entrance animation the
 * design system allows, one 0.9s rise-and-fade, once, and nothing on scroll.
 */
export function Panel({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`glass-prominent rounded-lg2 mcos-enter ${className}`}>{children}</section>
  );
}

/* ------------------------------------------------------------------ *
 * Buttons
 * ------------------------------------------------------------------ */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

/**
 * Per the 23 September production UI spec: "Primary actions: dark green fill,
 * white text. Secondary actions: white/transparent background, 1px green
 * border, green text." Sage is that green; Steel (ink/navy) is no longer a
 * button colour anywhere in the app.
 */
const BUTTON_CLASS: Record<ButtonVariant, string> = {
  // The mockups' deep green, sampled from every primary button in them.
  primary: 'bg-forest text-white hover:bg-forest-deep',
  secondary: 'bg-white text-forest border border-[#b9cfca] hover:bg-mint',
  ghost: 'text-ink-muted hover:bg-ink/5',
  // Destructive stays red. Amber is reserved for clinical flags and must never
  // double as a "danger" colour, or the two become indistinguishable.
  danger: 'bg-red-600 text-white hover:bg-red-700',
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled,
  loading,
  onClick,
  className = '',
  title,
}: {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  type?: 'button' | 'submit';
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  className?: string;
  title?: string;
}) {
  const sizeClass = size === 'sm' ? 'text-[13px] px-3.5 py-2' : 'text-[15px] px-5 py-2.5';
  return (
    <button
      type={type}
      title={title}
      disabled={disabled || loading}
      onClick={onClick}
      /*
        8px corners, as drawn. His spec asks for "restrained corner radius; do
        not turn every action into a pill", and every button in the mockups is
        a softly rounded rectangle rather than a pill or a sharp edge.
      */
      className={`inline-flex items-center justify-center gap-2 rounded-lg font-semibold
        transition-[transform,box-shadow,background-color] duration-150
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0
        motion-reduce:hover:translate-y-0
        ${BUTTON_CLASS[variant]} ${sizeClass} ${className}`}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}

export function Spinner({ className = '' }: { className?: string }) {
  return (
    <svg
      className={`animate-spin h-3.5 w-3.5 ${className}`}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12" cy="12" r="10"
        stroke="currentColor" strokeWidth="4" fill="none"
      />
      <path
        className="opacity-90"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * Pills and alert badges
 * ------------------------------------------------------------------ */

/**
 * Status tag.
 *
 * His system: a pill at 20px or more, moss-soft fill, and a dot beside the
 * label. The dot is not decoration, his accessibility floor requires that
 * "status is never color-only", so the label and the dot carry the meaning
 * together and a colourblind reader loses nothing.
 */
export function Pill({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      /*
        Padding and size straight from his spec: 5px 13px, 0.76rem, 600, and
        0.02em tracking. `leading-none` matters as much as the padding: without
        it the inherited line-height adds space above the text but not below,
        and the label sits visibly high in the pill.
      */
      className={`inline-flex items-center gap-1.5 rounded-full px-[13px] py-[5px] text-[0.76rem] font-semibold leading-none tracking-[0.02em] ${className}`}
    >
      {/* The dot his accessibility floor asks for, so status is never colour
          alone. `currentColor` keeps it matched to whatever tint is applied. */}
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {children}
    </span>
  );
}

/**
 * The three domain scores in one table cell, in place of the overall score.
 * Brian, 23 September: "Remove the Overall Score completely from both
 * clinician and patient dashboards. We should only show the individual
 * Metabolic, Inflammation, and Liver Health scores." The band colours are the
 * engine's, as on the printed report.
 */
export function DomainScorePills({
  metabolic,
  inflammation,
  liver,
}: {
  metabolic: number | null | undefined;
  inflammation: number | null | undefined;
  liver: number | null | undefined;
}) {
  const cells: Array<[string, string, number | null | undefined]> = [
    ['M', 'Metabolic', metabolic],
    ['I', 'Inflammation', inflammation],
    ['L', 'Liver health', liver],
  ];
  return (
    <span className="inline-flex items-center gap-1">
      {cells.map(([letter, name, value]) => (
        <span
          key={letter}
          title={`${name} ${value ?? 'not scored'}`}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[11.5px] font-semibold leading-none ${bandPillClass(value)}`}
        >
          <span className="opacity-60">{letter}</span>
          {value ?? '-'}
        </span>
      ))}
    </span>
  );
}

export function ScorePill({ score }: { score: number | null | undefined }) {
  return (
    <Pill className={bandPillClass(score)}>
      {score === null || score === undefined ? 'Incomplete' : `${score}/100`}
    </Pill>
  );
}

/**
 * The distinction that matters most in this product.
 *
 * A clinical alert means a dangerous lab value and a possible medical
 * emergency. A Retain alert means a patient may be about to cancel. A doctor
 * must never confuse the two, so they differ three ways at once, colour
 * (Amber vs Steel), a distinct glyph, and a spelled-out label.
 *
 * Colour alone would fail a colourblind clinician, which is why `kind` drives
 * all three and there is no prop for overriding one of them independently.
 */
export function AlertBadge({ kind }: { kind: 'clinical' | 'retain' }) {
  const clinical = kind === 'clinical';
  return (
    <Pill className={clinical ? 'bg-amber/20 text-amber-deep' : 'bg-steel/20 text-steel-deep'}>
      <svg
        width="12" height="12" viewBox="0 0 16 16" className="mr-1.5 shrink-0"
        aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
      >
        {clinical ? (
          /* Warning triangle, urgency, a thing to act on now. */
          <>
            <path d="M8 2.5 14.5 13.5h-13L8 2.5Z" />
            <path d="M8 6.75v3" />
            <path d="M8 11.75h.008" />
          </>
        ) : (
          /* Trend line, a pattern over time, not an emergency. */
          <path d="M1.75 11.5 5.5 7.25l3 2.5 5.25-5.75" />
        )}
      </svg>
      {clinical ? 'Clinical' : 'Retain'}
    </Pill>
  );
}

/**
 * A small (i) that holds an explanation until someone wants it.
 *
 * Dashboards accumulate sentences justifying themselves, how a number is
 * derived, why a list is ordered the way it is, and every one of them is read
 * once and then skipped forever, while continuing to cost vertical space and
 * making the screen look like documentation rather than an instrument. The
 * explanation is still worth having; it just does not belong in the layout.
 *
 * Hover or focus, so it is reachable by keyboard and not only by mouse.
 */
/**
 * A section that opens.
 *
 * The client asked that report data be "interactive and expandable so that the
 * amount of text is [not] overwhelming". A full report carries several pages of
 * material and a patient wants one part of it at a time, so every section
 * starts closed except the one that matters most.
 *
 * The panel animates on a grid row rather than a height, because a height
 * transition cannot interpolate to `auto` and the content height is unknown
 * until it renders. The chevron rotates rather than the label changing, so the
 * control does not move under the cursor.
 */
export function Disclosure({
  title,
  summary,
  hint,
  defaultOpen = false,
  children,
}: {
  title: string;
  summary?: string;
  hint?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="glass-standard rounded-md2 mb-4 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-4 px-5 py-4 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 font-display text-sm font-bold text-ink">
            {title}
            {hint && <InfoHint label={`About ${title}`}>{hint}</InfoHint>}
          </span>
          {summary && (
            <span className="mt-0.5 block text-sm text-ink-muted">{summary}</span>
          )}
        </span>

        <span
          aria-hidden="true"
          className={`shrink-0 text-ink-muted transition-transform duration-200 ease-out ${
            open ? 'rotate-90' : ''
          }`}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M6 4l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      <div
        className={`grid transition-all duration-200 ease-out ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="border-t border-ink/5 px-5 py-4">{children}</div>
        </div>
      </div>
    </section>
  );
}

/**
 * Keeps a centred hover tooltip on screen. On a phone an "i" near either edge
 * would otherwise open a bubble that runs off the side, so the bubble is
 * measured as it opens and slid back inside a 16px margin.
 */
export function useTooltipShift() {
  const ref = useRef<HTMLSpanElement>(null);
  const [shift, setShift] = useState(0);
  const measure = () => {
    const tip = ref.current;
    if (!tip) return;
    const rect = tip.getBoundingClientRect();
    const left = rect.left - shift;
    const right = rect.right - shift;
    const width = document.documentElement.clientWidth;
    const margin = 16;
    const next = left < margin ? margin - left : right > width - margin ? width - margin - right : 0;
    if (Math.round(next) !== Math.round(shift)) setShift(next);
  };
  // Also placed before it is ever shown, and again when the window resizes.
  const latest = useRef(measure);
  latest.current = measure;
  useEffect(() => {
    const run = () => latest.current();
    const frame = requestAnimationFrame(run);
    window.addEventListener('resize', run);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', run);
    };
  }, []);
  const style = shift ? { transform: `translateX(calc(-50% + ${Math.round(shift)}px))` } : undefined;
  return { ref, measure, style };
}

export function InfoHint({ label, children }: { label: string; children: ReactNode }) {
  const tip = useTooltipShift();
  return (
    <span className="group relative inline-flex align-middle" onPointerEnter={tip.measure} onFocus={tip.measure}>
      <button
        type="button"
        aria-label={label}
        className="grid h-4 w-4 place-items-center rounded-full border border-ink/15 text-[10px] font-semibold leading-none text-ink-muted transition-colors hover:border-ink/30 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-steel/40"
      >
        i
      </button>
      <span
        ref={tip.ref}
        style={tip.style}
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-64 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-sm2 bg-ink px-3 py-2 text-[11px] leading-relaxed text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {children}
      </span>
    </span>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="text-center py-12 px-6">
      <p className="font-display text-sm font-semibold text-ink">{title}</p>
      {body && <p className="mt-1.5 text-sm text-ink-muted max-w-md mx-auto">{body}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const message =
    error instanceof Error ? error.message : 'Something went wrong loading this data.';
  return (
    <div className="rounded-sm2 border border-red-200 bg-red-50/80 p-4">
      <p className="text-sm font-semibold text-red-800">Could not load</p>
      <p className="mt-1 text-sm text-red-700">{message}</p>
      {retry && (
        <div className="mt-3">
          <Button variant="secondary" size="sm" onClick={retry}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}

export function LoadingRows({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2.5 p-1" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="h-9 rounded-sm2 bg-white/50 animate-pulse" />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Stat card, the four tiles across the top of the dashboard
 * ------------------------------------------------------------------ */

export type StatTone = 'blue' | 'indigo' | 'green' | 'rose';

/**
 * The four tones predate V3, where colour is fully assigned and a fourth
 * accent is explicitly disallowed. They therefore now map onto the sanctioned
 * palette rather than introducing new hues: Sage only where the number is
 * genuinely positive, Steel everywhere else, Amber for clinical counts.
 */
const TONE_CLASS: Record<StatTone, string> = {
  blue: 'bg-steel/10 text-steel-deep',
  indigo: 'bg-steel/10 text-steel-deep',
  green: 'bg-sage/20 text-sage-deep',
  rose: 'bg-amber/20 text-amber-deep',
};

export function StatCard({
  label,
  value,
  delta,
  deltaLabel,
  tone = 'blue',
  icon,
}: {
  label: string;
  value: string | number | null;
  delta?: number | null;
  deltaLabel?: string;
  tone?: StatTone;
  icon: ReactNode;
}) {
  return (
    <div className="glass-standard mcos-lift rounded-md2 p-5 flex items-start gap-4">
      <span
        className={`shrink-0 grid place-items-center h-10 w-10 rounded-sm2 ${TONE_CLASS[tone]}`}
        aria-hidden="true"
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-xs text-ink-muted">{label}</p>
        <p className="font-display text-2xl font-bold text-ink leading-tight mt-0.5">
          {value ?? '-'}
        </p>
        {(delta !== undefined || deltaLabel) && (
          <p className="text-[11px] mt-0.5 text-ink-muted">
            {delta !== undefined && delta !== null && (
              <span className={delta > 0 ? 'text-sage-deep font-semibold' : 'text-ink-muted'}>
                {formatDelta(delta)}{' '}
              </span>
            )}
            {deltaLabel}
          </p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Score gauge, the in-app counterpart of the report dial
 * ------------------------------------------------------------------ */

/**
 * Compact arc gauge. Same semantics as the report gauge: a neutral track with a
 * band-coloured fill proportional to the score.
 *
 * The band colours are engine semantics shared with the printed PDF and are
 * deliberately NOT restyled to the V3 palette, a band means the same thing on
 * screen as it does on paper.
 */
export function ScoreGauge({
  score,
  label,
  size = 96,
}: {
  score: number | null | undefined;
  label?: string;
  size?: number;
}) {
  const START = -120;
  const SWEEP = 240;
  const radius = 34;
  const cx = 44;
  const cy = 40;
  const width = 9;

  const polar = (deg: number) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  };
  const arc = (from: number, to: number) => {
    const a = polar(from);
    const b = polar(to);
    const large = Math.abs(to - from) > 180 ? 1 : 0;
    return `M ${a.x.toFixed(2)} ${a.y.toFixed(2)} A ${radius} ${radius} 0 ${large} 1 ${b.x.toFixed(2)} ${b.y.toFixed(2)}`;
  };

  const value = score ?? 0;
  const end = START + (Math.min(100, Math.max(0, value)) / 100) * SWEEP;

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size * 0.78} viewBox="0 0 88 70" role="img"
        aria-label={score === null || score === undefined ? 'Incomplete' : `${score} out of 100`}>
        <path d={arc(START, START + SWEEP)} fill="none" stroke="rgba(27,36,48,0.10)"
          strokeWidth={width} strokeLinecap="round" />
        {score !== null && score !== undefined && (
          <path d={arc(START, end)} fill="none" stroke={bandColor(score)}
            strokeWidth={width} strokeLinecap="round" />
        )}
        <text x={cx} y={cy + 6} textAnchor="middle"
          fill="var(--ink)" style={{ fontSize: 20, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
          {score ?? '-'}
        </text>
        <text x={cx} y={cy + 19} textAnchor="middle"
          fill="var(--ink-muted)" style={{ fontSize: 8 }}>
          /100
        </text>
      </svg>
      {label && <p className="text-[11px] font-medium text-ink-muted -mt-1">{label}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Table
 * ------------------------------------------------------------------ */

/** `minWidth` for a table with many columns: below it the table scrolls sideways instead of squeezing every cell. */
export function Table({ children, minWidth }: { children: ReactNode; minWidth?: number }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" style={minWidth ? { minWidth } : undefined}>{children}</table>
    </div>
  );
}

export function Th({
  children,
  align = 'left',
}: {
  children: ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-ink-muted border-b border-ink/10 text-${align} max-sm:whitespace-nowrap`}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = 'left',
  className = '',
}: {
  children: ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
}) {
  return (
    <td className={`px-5 py-3 border-b border-ink/5 text-${align} ${className}`}>
      {children}
    </td>
  );
}

/* ------------------------------------------------------------------ *
 * Forms
 * ------------------------------------------------------------------ */

export function Field({
  label,
  hint,
  error,
  children,
  required,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-ink mb-1.5">
        {label}
        {required && <span className="text-amber-deep ml-0.5">*</span>}
      </span>
      {children}
      {hint && !error && <span className="block mt-1 text-[11px] text-ink-muted">{hint}</span>}
      {error && <span className="block mt-1 text-[11px] text-red-600">{error}</span>}
    </label>
  );
}

export const inputClass =
  'w-full rounded-control border border-ink/10 bg-white/60 px-4 py-3 text-sm text-ink ' +
  'placeholder:text-ink-muted focus:border-steel/40';

/* ------------------------------------------------------------------ *
 * Searchable select
 *
 * A native select stops being usable somewhere around twenty options: the goal
 * list has twenty six and the treatment list will have twenty seven, and
 * neither can be typed into. This is the same control with a filter on top.
 *
 * It is still a closed list. Typing filters, it never creates a value, because
 * everything downstream matches on the exact tag and a free-text entry would
 * match nothing while looking perfectly correct on screen.
 * ------------------------------------------------------------------ */

export interface SelectOption {
  value: string;
  label: string;
}

export function SearchableSelect({
  value,
  options,
  onChange,
  placeholder = 'Search',
  emptyLabel = 'Not recorded',
  disabled,
}: {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((option) => option.value === value) ?? null;

  const filtered = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) return options;
    return options.filter((option) => option.label.toLowerCase().includes(text));
  }, [options, query]);

  /* Close on a click anywhere else: the behaviour every other dropdown on the
     web has, and the one people try first when they change their mind. */
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else setQuery('');
    setActive(0);
  }, [open]);

  function choose(next: string) {
    onChange(next);
    setOpen(false);
  }

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
        className={`${inputClass} flex items-center justify-between gap-2 text-left disabled:opacity-50 disabled:cursor-not-allowed`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected ? 'text-ink' : 'text-ink-muted'}>
          {selected ? selected.label : emptyLabel}
        </span>
        <span
          aria-hidden="true"
          className={`shrink-0 text-ink-muted transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 4.5 6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="glass-standard absolute z-30 mt-1 w-full rounded-control overflow-hidden shadow-[0_8px_24px_rgba(20,23,28,0.12)]">
          <input
            ref={inputRef}
            value={query}
            placeholder={placeholder}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActive((index) => Math.min(index + 1, filtered.length - 1));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActive((index) => Math.max(index - 1, 0));
              } else if (event.key === 'Enter') {
                event.preventDefault();
                const option = filtered[active];
                if (option) choose(option.value);
              } else if (event.key === 'Escape') {
                setOpen(false);
              }
            }}
            className="w-full border-b border-ink/10 bg-transparent px-4 py-2.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none"
          />

          <ul role="listbox" className="max-h-56 overflow-y-auto py-1">
            <li>
              <button
                type="button"
                onClick={() => choose('')}
                className="w-full px-4 py-2 text-left text-sm text-ink-muted hover:bg-ink/5"
              >
                {emptyLabel}
              </button>
            </li>
            {filtered.map((option, index) => (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => choose(option.value)}
                  className={`w-full px-4 py-2 text-left text-sm ${index === active ? 'bg-ink/5' : ''} ${option.value === value ? 'font-semibold text-ink' : 'text-ink'}`}
                >
                  {option.label}
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-4 py-3 text-sm text-ink-muted">Nothing matches that</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Confirmation
 *
 * For the small number of actions that cannot be undone from the screen that
 * started them. Enrolment is the first: it begins a recurring charge, and a
 * misplaced click costs the clinic money and the patient an explanation.
 *
 * The consequence is spelled out in the dialog rather than in a hint beside
 * the button, because a hint is read by people who are already unsure and this
 * has to reach the people who are not.
 * ------------------------------------------------------------------ */

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  loading,
  error,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return overlay(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div className="glass-standard w-full max-w-md rounded-md2 p-6 shadow-[0_16px_48px_rgba(20,23,28,0.25)]">
        <h2 className="text-base font-semibold text-ink mb-2">{title}</h2>
        <div className="text-sm text-ink-muted space-y-2">{body}</div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink">{label}</p>
        {description && <p className="text-xs text-ink-muted mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition
          disabled:opacity-50 ${checked ? 'bg-steel' : 'bg-ink/20'}`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition
            ${checked ? 'translate-x-[1.20rem]' : 'translate-x-[0.15rem]'}`}
        />
      </button>
    </div>
  );
}

/** Trend arrow for tables, direction shows movement, colour shows quality. */
export function TrendArrow({
  direction,
  quality,
}: {
  direction: 'up' | 'down' | 'flat' | null;
  quality: 'improved' | 'no_change' | 'worsened' | null;
}) {
  const color =
    quality === 'improved'
      ? 'var(--band-optimal)'
      : quality === 'worsened'
        ? 'var(--band-risk)'
        : quality === 'no_change'
          ? 'var(--band-warn)'
          : 'var(--ink-muted)';

  if (direction === null || direction === 'flat') {
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" aria-label="no change">
        <line x1="3.5" y1="8" x2="12.5" y2="8" stroke={color} strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-label={direction}>
      <path
        d={direction === 'down' ? 'M8 3.2v9.6M4.4 9.2 8 12.8l3.6-3.6' : 'M8 12.8V3.2M4.4 6.8 8 3.2l3.6 3.6'}
        fill="none" stroke={color} strokeWidth="1.9"
        strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * Avatar and icon bubble, the two round shapes every mockup repeats
 * ------------------------------------------------------------------ */

const AVATAR_SIZE = {
  sm: 'h-10 w-10 text-[15px]',
  md: 'h-12 w-12 text-[17px]',
  lg: 'h-14 w-14 text-[19px]',
  xl: 'h-20 w-20 text-[30px]',
  '2xl': 'h-24 w-24 text-[36px]',
} as const;

/**
 * Initials in a circle, Fraunces, as on every patient row and header in the
 * 23 September mockups. `tone` picks the fill the mockup uses in that place:
 * the pale grey of a list row or the soft green of a selected patient.
 */
export function Avatar({
  firstName,
  lastName,
  size = 'lg',
  tone = 'neutral',
  className = '',
}: {
  firstName: string;
  lastName: string;
  size?: keyof typeof AVATAR_SIZE;
  tone?: 'neutral' | 'mint';
  className?: string;
}) {
  const letters = `${firstName.trim().charAt(0)}${lastName.trim().charAt(0)}`.toUpperCase();
  return (
    <span
      aria-hidden="true"
      className={`inline-grid shrink-0 place-items-center rounded-full font-display ${AVATAR_SIZE[size]} ${
        tone === 'mint' ? 'bg-mint text-forest' : 'bg-[#eef0ee] text-navy'
      } ${className}`}
    >
      {letters || '-'}
    </span>
  );
}

const BUBBLE_TONE = {
  mint: 'bg-mint text-forest',
  red: 'bg-alert-soft text-alert',
  orange: 'bg-risk-orange-soft text-risk-orange',
  yellow: 'bg-risk-yellow-soft text-risk-yellow',
  blue: 'bg-info-soft text-info',
  clay: 'bg-[var(--amber-soft)] text-[var(--amber-deep)]',
  grey: 'bg-[#eef0ee] text-navy',
} as const;

/** A round tinted tile holding one line icon: KPI cards, stat rows, reasons. */
export function IconBubble({
  children,
  tone = 'mint',
  size = 48,
  className = '',
}: {
  children: ReactNode;
  tone?: keyof typeof BUBBLE_TONE;
  size?: number;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size }}
      className={`inline-grid shrink-0 place-items-center rounded-full ${BUBBLE_TONE[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

/**
 * Render a modal at the top of the app instead of where it is declared.
 *
 * A "fixed" overlay is positioned against the nearest ancestor that has a
 * backdrop blur, filter or transform, and several cards have one, so a modal
 * declared inside them was trapped in the card instead of covering the screen.
 * Portalled into the scaled app shell when present, so it keeps the large
 * screen scaling, otherwise into the body.
 */
export function overlay(node: ReactNode) {
  const target = (typeof document !== 'undefined' && document.querySelector('.app-scale')) || document.body;
  return createPortal(node, target);
}
